import {useCallback, useEffect, useMemo, useState} from "react";
import {Package, Truck} from "lucide-react";
import {useSearchParams} from "react-router-dom";
import {
  getAdminOrder,
  getAdminOrderKpis,
  listAdminOrders,
  generateAdminAwbSameday,
  removeAdminOrderDiscount,
  sendAdminSymphonyaOrders,
  symphonyaOrderStatus,
  symphonyaResendConfirm,
  updateAdminOrderDelivery,
  updateAdminOrderLines,
  updateAdminOrderStatus,
  type AdminOrderDetail,
  type AdminOrderKpis,
  type AdminOrderSummary
} from "@corporatesclub/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {adminDateRangeToApi, EMPTY_ADMIN_DATE_RANGE, type AdminDateRangeValue} from "@/lib/adminDateRange";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {OrderDetailModal} from "./orders/OrderDetailModal";
import {OrderKpiCards} from "./orders/OrderKpiCards";
import {SymphonyaOrderBadge} from "./orders/SymphonyaOrderBadge";
import {AdminAlert, AdminBadge, AdminDateRangePicker, AdminFilterBar, AdminFilterField, AdminModernTable, AdminPageLayout, AdminPagination, type AdminTableColumn} from "./ui";

const STATUS_OPTS = [
  {v: 0, l: "All"},
  {v: 1, l: "PROCESSING"},
  {v: 2, l: "SHIPPED"},
  {v: 3, l: "DELIVERED"},
  {v: 4, l: "CANCELLED"},
  {v: 5, l: "PAID"},
  {v: 6, l: "PAYMENT ERROR"},
  {v: 7, l: "READY"}
];

const EMPTY_CELL = <span className="text-xs text-muted-foreground">—</span>;

export function OrdersView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<AdminOrderSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState(0);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<AdminDateRangeValue>(EMPTY_ADMIN_DATE_RANGE);
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminOrderDetail | null>(null);
  const [deliveryForm, setDeliveryForm] = useState({awb: "", cost: "", adresa: "", adresa_localitate: "", adresa_codpostal: "", curier: 50});
  const [err, setErr] = useState("");
  const [kpis, setKpis] = useState<AdminOrderKpis | null>(null);
  const [symphonyaSending, setSymphonyaSending] = useState(false);

  const loadKpis = useCallback(() => {
    getAdminOrderKpis().then(setKpis).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const dates = adminDateRangeToApi(dateRange);
    listAdminOrders({
      page,
      pageSize: 20,
      status: statusFilter || undefined,
      search: search || undefined,
      dateFrom: dates.dateFrom,
      dateTo: dates.dateTo
    }).then((r) => {
      setItems(r.items);
      setTotalPages(r.totalPages);
    }).catch((e: Error) => setErr(e.message));
  }, [page, statusFilter, search, dateRange]);

  useEffect(() => { load(); }, [page, statusFilter, dateRange]);
  useEffect(() => { loadKpis(); }, [loadKpis]);

  function filterByStatus(status: number) {
    setStatusFilter(status);
    setPage(1);
  }

  function resetFilters() {
    setStatusFilter(0);
    setSearch("");
    setDateRange(EMPTY_ADMIN_DATE_RANGE);
    setPage(1);
  }

  async function openOrder(id: number) {
    setOpenId(id);
    setModalOpen(true);
    const o = await getAdminOrder(id);
    setDetail(o);
    setDeliveryForm({
      awb: o.delivery.awb || "",
      cost: String(o.delivery.shippingCost ?? 0),
      adresa: o.delivery.adresa || "",
      adresa_localitate: o.delivery.localitate || "",
      adresa_codpostal: o.delivery.codPostal || "",
      curier: o.delivery.curier || 50
    });
  }

  useEffect(() => {
    const raw = searchParams.get("open");
    if(!raw) return;
    const id = Number(raw);
    if(!Number.isFinite(id) || id <= 0) return;
    openOrder(id).catch((e: Error) => adminToast.error(e.message || "Could not open order"));
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, {replace: true});
  }, []);

  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) { setOpenId(null); setDetail(null); }
  }

  async function refreshDetail(id: number) {
    setDetail(await getAdminOrder(id));
  }

  function applyStatus(id: number, op: string, label: string) {
    confirm(`Confirmi schimbarea statusului in "${label}"?`, async () => {
      await updateAdminOrderStatus(id, op);
      load();
      loadKpis();
      await refreshDetail(id);
      adminToast.success("Status updated.");
    });
  }

  async function saveDelivery(id: number) {
    await updateAdminOrderDelivery(id, {
      curier: deliveryForm.curier,
      awb: deliveryForm.awb,
      cost: parseFloat(deliveryForm.cost) || 0,
      adresa: deliveryForm.adresa,
      adresa_localitate: deliveryForm.adresa_localitate,
      adresa_codpostal: deliveryForm.adresa_codpostal,
      adresa_judet: detail?.delivery.judet
    });
    load();
    loadKpis();
    await refreshDetail(id);
    adminToast.success("Delivery updated.");
  }

  function removeDiscount(discountId: number, orderId: number) {
    confirm("Remove discount from order?", async () => {
      await removeAdminOrderDiscount(discountId);
      load();
      await refreshDetail(orderId);
      adminToast.success("Discount removed.");
    });
  }

  async function genAwbSameday(id: number) {
    try {
      const r = await generateAdminAwbSameday(id);
      adminToast[r.awb ? "success" : "error"](r.awb ? `AWB SameDay: ${r.awb}` : (r.message || "Error AWB"));
      load();
      loadKpis();
      await refreshDetail(id);
    } catch(e: unknown) {
      adminToast.error(e instanceof Error ? e.message : "Error AWB");
    }
  }

  async function saveLines(id: number, lines: {productId: number; quantity: number}[]) {
    await updateAdminOrderLines(id, lines);
    load();
    await refreshDetail(id);
    adminToast.success("Products updated.");
  }

  function resendSymphonya() {
    if(!detail) return;
    const {title, message, confirmLabel} = symphonyaResendConfirm(detail.symphonya, detail.id);
    confirm(message, async () => {
      setSymphonyaSending(true);
      try {
        const r = await sendAdminSymphonyaOrders(detail.id);
        if(r.ok) {
          const erpId = r.symphonya && r.symphonya > 1 ? r.symphonya : detail.symphonya;
          adminToast.success(erpId > 1 ? `Synced — Symphonya ERP #${erpId}` : "Order sent to Symphonya.");
        } else {
          adminToast.error(r.symphonyaError || r.error || "Symphonya push failed.");
        }
        load();
        await refreshDetail(detail.id);
      } finally {
        setSymphonyaSending(false);
      }
    }, title, confirmLabel);
  }

  const orderColumns = useMemo((): AdminTableColumn<AdminOrderSummary>[] => [
    {
      key: "id",
      header: "Order",
      headerTitle: "Order ID",
      className: "w-[4.5rem] whitespace-nowrap",
      render: (o) => <span className="admin-modern-table__id">#{o.id}</span>
    },
    {
      key: "customer",
      header: "Customer",
      headerTitle: "Customer name and email",
      className: "min-w-[10rem]",
      render: (o) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-foreground">{o.nume}</div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{o.email}</div>
        </div>
      )
    },
    {
      key: "status",
      header: "Status",
      headerTitle: "Order status",
      className: "whitespace-nowrap",
      render: (o) => <AdminBadge status={o.status}>{o.statusLabel}</AdminBadge>
    },
    {
      key: "guest",
      header: "Guest",
      headerTitle: "Guest checkout (RAPIDA)",
      className: "whitespace-nowrap",
      hideBelow: "lg",
      render: (o) => (o.guest ? <AdminBadge variant="outline">RAPIDA</AdminBadge> : EMPTY_CELL)
    },
    {
      key: "awb",
      header: "AWB",
      headerTitle: "Shipping AWB number",
      className: "whitespace-nowrap",
      hideBelow: "lg",
      render: (o) => (o.awb ? (
        <span className="inline-flex items-center gap-1 text-xs" title={o.awb}>
          <Truck className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="max-w-[5rem] truncate tabular-nums">{o.awb}</span>
        </span>
      ) : EMPTY_CELL)
    },
    {
      key: "qty",
      header: "Qty",
      headerTitle: "Total product quantity",
      className: "whitespace-nowrap text-center",
      render: (o) => (
        <span className="inline-flex items-center gap-1 tabular-nums text-sm">
          <Package className="h-3.5 w-3.5 text-muted-foreground" />
          {o.productCount}
        </span>
      )
    },
    {
      key: "payment",
      header: "Payment",
      headerTitle: "Payment method",
      className: "whitespace-nowrap",
      hideBelow: "md",
      render: (o) => <AdminBadge variant="outline">{o.payment}</AdminBadge>
    },
    {
      key: "date",
      header: "Date",
      headerTitle: "Order date and time",
      className: "whitespace-nowrap",
      hideBelow: "md",
      render: (o) => <span className="text-sm tabular-nums text-muted-foreground">{o.dateLabel}</span>
    },
    {
      key: "symphonya",
      header: "Symphonya",
      headerTitle: "Symphonya ERP sync status (- pending, ERR failed, #id synced)",
      className: "whitespace-nowrap",
      render: (o) => <SymphonyaOrderBadge symphonya={o.symphonya} />
    }
  ], []);

  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;

  return (
    <AdminPageLayout
      title="Orders"
      description="Manage store orders"
      breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "Orders"}]}
    >
      {confirmDialog}
      {kpis ? (
        <OrderKpiCards
          kpis={kpis}
          activeStatus={statusFilter}
          onFilterStatus={filterByStatus}
        />
      ) : null}
      <AdminFilterBar
        onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}
        onReset={resetFilters}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Name, email, AWB..."
        itemCount={items.length}
      >
        <AdminFilterField label="Period">
          <AdminDateRangePicker value={dateRange} onChange={setDateRange} />
        </AdminFilterField>
        <AdminFilterField label="Status">
          <Select value={String(statusFilter)} onValueChange={(v) => setStatusFilter(Number(v))}>
            <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </AdminFilterField>
      </AdminFilterBar>
      <AdminModernTable
        items={items}
        getId={(o) => o.id}
        getTitle={(o) => o.nume}
        getSubtitle={(o) => o.email}
        columns={orderColumns}
        getSearchText={(o) => adminSearchText(
          o.id,
          o.nume,
          o.email,
          o.awb,
          o.statusLabel,
          o.payment,
          o.dateLabel,
          o.total,
          o.productCount,
          symphonyaOrderStatus(o.symphonya).label
        )}
        selectedId={openId}
        onRowClick={(o) => openOrder(o.id)}
        emptyTitle="No orders"
        emptyDescription="Adjust filters or check back later."
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={() => { setPage(1); load(); }}
        clientFilter={false}
        hideSearch
        className="admin-modern-table--orders"
      />
      <OrderDetailModal
        open={modalOpen}
        onOpenChange={closeModal}
        detail={detail}
        deliveryForm={deliveryForm}
        setDeliveryForm={setDeliveryForm}
        onApplyStatus={applyStatus}
        onSaveDelivery={saveDelivery}
        onRemoveDiscount={removeDiscount}
        onGenAwbSameday={genAwbSameday}
        onSaveLines={saveLines}
        symphonyaSending={symphonyaSending}
        onResendSymphonya={resendSymphonya}
      />
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
